import { useState } from 'react';
import { useApiKeys, useCreateApiKey, useDeleteApiKey, useUser } from '../hooks/useApi';
import type { ApiKeyCreateResponse } from '../types';
import { PLAN_DETAILS } from '../types';
import { ConfirmModal } from '../components/ConfirmModal';
import { useTranslation, Trans } from 'react-i18next';
import { useFormatDate } from '../hooks/useFormatDate';

export function ApiKeysPage() {
  const { t } = useTranslation('settings');
  const formatDate = useFormatDate();
  const { data: user } = useUser();
  const { data: keys, isLoading } = useApiKeys();
  const { mutate: createKey, isPending: creating } = useCreateApiKey();
  const { mutate: deleteKey } = useDeleteApiKey();
  const [newKeyName, setNewKeyName] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreateResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const plan = user?.plan ?? 'free';
  const apiKeyLimit = PLAN_DETAILS[plan].apiKeyLimit;
  const atKeyLimit = (keys?.length ?? 0) >= apiKeyLimit;

  // Confirm modal state
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  function handleCreate() {
    createKey(
      { name: newKeyName || 'Default', scopes: readOnly ? ['read'] : ['read', 'write'] },
      {
        onSuccess: (data) => {
          setCreatedKey(data);
          setNewKeyName('');
          setReadOnly(false);
          setShowCreate(false);
        }
      }
    );
  }

  function handleCopy(key: string) {
    navigator.clipboard.writeText(key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="min-h-full p-4 sm:p-8 animate-fade-in">
      <div className="page-narrow">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('apiKeys.title')}</h1>
            <p className="mt-1 text-sm text-gray-500">
              <Trans i18nKey="apiKeys.subtitle" ns="settings" components={{ code: <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono text-primary-700" /> }} />
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            disabled={atKeyLimit}
            className="rounded-xl bg-primary-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98] flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('apiKeys.createKey')}
          </button>
        </div>

        {atKeyLimit && (
          <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3">
            <p className="text-xs text-amber-800">
              {t('apiKeys.keyLimitReached', { count: keys?.length, limit: apiKeyLimit })}{' '}
              {plan === 'free' && <a href="/pricing" className="font-semibold underline hover:text-amber-900">{t('apiKeys.upgradeForMore')}</a>}{' '}
              {plan === 'free' && t('apiKeys.forMoreKeys')}
            </p>
          </div>
        )}

        {/* Created key banner */}
        {createdKey && (
          <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
            <p className="text-xs font-semibold text-emerald-800 mb-2">
              {t('apiKeys.newKeyCreated')}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-lg bg-white border border-emerald-200 px-3 py-2.5 text-sm font-mono text-gray-900 select-all break-all">
                {createdKey.apiKey}
              </code>
              <button
                onClick={() => handleCopy(createdKey.apiKey)}
                className="rounded-lg border border-emerald-200 bg-white px-4 py-2.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors flex-shrink-0"
              >
                {copied ? t('common:buttons.copied') : t('common:buttons.copy')}
              </button>
            </div>
            <button
              onClick={() => setCreatedKey(null)}
              className="mt-3 text-xs text-emerald-600 hover:text-emerald-800"
            >
              {t('apiKeys.dismiss')}
            </button>
          </div>
        )}

        {/* Create form */}
        {showCreate && (
          <div className="mb-5 rounded-2xl border border-gray-200 bg-gray-50 p-5">
            <label className="block text-xs font-medium text-gray-700 mb-2">{t('apiKeys.keyName')}</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder={t('apiKeys.keyNamePlaceholder')}
                className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
              <button
                onClick={handleCreate}
                disabled={creating}
                className="rounded-lg bg-primary-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {creating ? t('apiKeys.creating') : t('apiKeys.create')}
              </button>
              <button
                onClick={() => { setShowCreate(false); setNewKeyName(''); setReadOnly(false); }}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {t('apiKeys.cancel')}
              </button>
            </div>
            {/* Access scope: full-access by default, or read-only (GET-only) */}
            <label className="mt-3 flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={readOnly}
                onChange={(e) => setReadOnly(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-400"
              />
              <span>{t('apiKeys.readOnlyLabel')}</span>
            </label>
          </div>
        )}

        {/* Keys list */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 rounded-2xl border border-gray-200 bg-white animate-pulse" />
            ))}
          </div>
        ) : keys && keys.length > 0 ? (
          <div className="space-y-3">
            {keys.map((k) => (
              <div
                key={k.keyPrefix + k.createdAt}
                className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-card"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <code className="text-sm font-mono text-gray-700">{k.keyPrefix}...</code>
                    <span className="text-sm font-medium text-gray-900">{k.keyName}</span>
                    {k.scopes && !k.scopes.includes('write') && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        {t('apiKeys.readOnlyBadge')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1">
                    <span className="text-xs text-gray-400">
                      {t('apiKeys.created', { date: formatDate(k.createdAt) })}
                    </span>
                    {k.lastUsedAt && (
                      <span className="text-xs text-gray-400">
                        {t('apiKeys.lastUsed', { date: formatDate(k.lastUsedAt) })}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setConfirmAction({
                      title: t('apiKeys.deleteTitle'),
                      message: t('apiKeys.deleteMessage', { prefix: k.keyPrefix }),
                      onConfirm: () => { deleteKey(k.keyPrefix); setConfirmAction(null); }
                    });
                  }}
                  className="rounded-lg border border-red-100 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors flex-shrink-0"
                >
                  {t('apiKeys.delete')}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-card">
            <p className="text-sm text-gray-400">{t('apiKeys.noKeys')}</p>
          </div>
        )}

      </div>

      <ConfirmModal
        open={!!confirmAction}
        title={confirmAction?.title ?? ''}
        message={confirmAction?.message ?? ''}
        confirmLabel={t('apiKeys.confirmProceed')}
        onConfirm={() => confirmAction?.onConfirm()}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}
